import { apiAuth, apiResponseType } from "./index";

export interface PreSaleFilters {
  pre_sale_status_id?: number | number[];
  unity_id?: number | number[];
  seller_id?: number | number[];
  created_by?: number;
  start_date?: string;
  end_date?: string;
  per_page?: number;
  sale_id?: number;
  sale_type_id?: number | number[];
  payment_form_id?: number | number[];
  lottery_id?: number | number[];
  contest_id?: number | number[];
  client_id?: number;
  client_name?: string;
  receipt_status?: "confirmed" | "pending";
  order_by?: string;
  order_direction?: "asc" | "desc";
}

// Listar pré-vendas
export const getPreSales = (
  page = 1,
  params?: PreSaleFilters
): apiResponseType => {
  const queryParams = new URLSearchParams();
  queryParams.append("page", page.toString());

  if (params?.pre_sale_status_id) {
    if (Array.isArray(params.pre_sale_status_id)) {
      params.pre_sale_status_id.forEach((id) => {
        queryParams.append("pre_sale_status_id[]", id.toString());
      });
    } else {
      queryParams.append("pre_sale_status_id", params.pre_sale_status_id.toString());
    }
  }

  if (params?.unity_id) {
    if (Array.isArray(params.unity_id)) {
      params.unity_id.forEach((id) => {
        queryParams.append("unity_id[]", id.toString());
      });
    } else {
      queryParams.append("unity_id", params.unity_id.toString());
    }
  }

  if (params?.seller_id) {
    if (Array.isArray(params.seller_id)) {
      params.seller_id.forEach((id) => {
        queryParams.append("seller_id[]", id.toString());
      });
    } else {
      queryParams.append("seller_id", params.seller_id.toString());
    }
  }

  if (params?.created_by) {
    queryParams.append("created_by", params.created_by.toString());
  }

  if (params?.start_date) {
    queryParams.append("start_date", params.start_date);
  }

  if (params?.end_date) {
    queryParams.append("end_date", params.end_date);
  }

  if (params?.per_page) {
    queryParams.append("per_page", params.per_page.toString());
  }

  if (params?.sale_id) {
    queryParams.append("sale_id", params.sale_id.toString());
  }

  if (params?.sale_type_id) {
    if (Array.isArray(params.sale_type_id)) {
      params.sale_type_id.forEach((id) => {
        queryParams.append("sale_type_id[]", id.toString());
      });
    } else {
      queryParams.append("sale_type_id", params.sale_type_id.toString());
    }
  }

  if (params?.payment_form_id) {
    if (Array.isArray(params.payment_form_id)) {
      params.payment_form_id.forEach((id) => {
        queryParams.append("payment_form_id[]", id.toString());
      });
    } else {
      queryParams.append("payment_form_id", params.payment_form_id.toString());
    }
  }

  if (params?.lottery_id) {
    if (Array.isArray(params.lottery_id)) {
      params.lottery_id.forEach((id) => {
        queryParams.append("lottery_id[]", id.toString());
      });
    } else {
      queryParams.append("lottery_id", params.lottery_id.toString());
    }
  }

  if (params?.contest_id) {
    if (Array.isArray(params.contest_id)) {
      params.contest_id.forEach((id) => {
        queryParams.append("contest_id[]", id.toString());
      });
    } else {
      queryParams.append("contest_id", params.contest_id.toString());
    }
  }

  if (params?.client_id) {
    queryParams.append("client_id", params.client_id.toString());
  }

  if (params?.client_name) {
    queryParams.append("client_name", params.client_name);
  }

  if (params?.receipt_status) {
    queryParams.append("receipt_status", params.receipt_status);
  }

  if (params?.order_by) {
    queryParams.append("order_by", params.order_by);
  }

  if (params?.order_direction) {
    queryParams.append("order_direction", params.order_direction);
  }

  return apiAuth.get(`pre-sales?${queryParams.toString()}`);
};

// Detalhes de uma pré-venda
export const getPreSale = (uuid: string): apiResponseType =>
  apiAuth.get(`pre-sales/${uuid}`);

// Arquivos de uma pré-venda
export const getPreSaleFiles = (uuid: string): apiResponseType =>
  apiAuth.get(`pre-sales/${uuid}/files`);

// Quantidade de pré-vendas pendentes
export const getPreSalesPendingCount = (
  unityIds?: number[]
): apiResponseType => {
  const queryParams = new URLSearchParams();

  if (unityIds && unityIds.length > 0) {
    unityIds.forEach((id) => {
      queryParams.append("unity_id[]", id.toString());
    });
  }

  return apiAuth.get(`pre-sales/pending-count?${queryParams.toString()}`);
};

// Aprovar pré-venda (fotos opcionais - já enviadas na criação)
export const approvePreSale = (
  uuid: string,
  files?: File[]
): apiResponseType => {
  const formData = new FormData();

  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append("files[]", file);
    });
  }

  return apiAuth.post(`pre-sales/${uuid}/approve`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// Rejeitar pré-venda
export const rejectPreSale = (
  uuid: string,
  data?: { rejection_reason?: string }
): apiResponseType =>
  apiAuth.post(`pre-sales/${uuid}/reject`, data || {});

// Colocar pré-venda em análise (não movimenta estoque)
export const analyzePreSale = (
  uuid: string,
  data?: { analysis_reason?: string }
): apiResponseType =>
  apiAuth.post(`pre-sales/${uuid}/analyze`, data || {});

// Notificações pendentes do vendedor logado (Em Análise/Rejeitado ainda não confirmadas)
export const getSellerPreSaleNotifications = (): apiResponseType =>
  apiAuth.get("pre-sales/seller-notifications");

// Confirma a ciência do vendedor sobre a mudança de status
export const acknowledgePreSaleNotification = (
  uuid: string
): apiResponseType => apiAuth.post(`pre-sales/${uuid}/acknowledge`, {});

// Confirma o recebimento físico do bolão pelo vendedor
// (independente de acknowledgePreSaleNotification, que trata da ciência de status)
export const confirmPreSaleReceipt = (
  uuid: string,
  payload?: { received_note?: string }
): apiResponseType =>
  apiAuth.post(`pre-sales/${uuid}/confirm-receipt`, payload || {});

// Constantes de status
export const PRE_SALE_STATUS = {
  PENDING: 1,
  APPROVED: 2,
  REJECTED: 3,
  IN_ANALYSIS: 4,
} as const;

export const PRE_SALE_STATUS_LABELS: Record<number, string> = {
  [PRE_SALE_STATUS.PENDING]: "Pendente",
  [PRE_SALE_STATUS.APPROVED]: "Aprovado",
  [PRE_SALE_STATUS.REJECTED]: "Rejeitado",
  [PRE_SALE_STATUS.IN_ANALYSIS]: "Em Análise",
};

export const PRE_SALE_STATUS_COLORS: Record<number, string> = {
  [PRE_SALE_STATUS.PENDING]: "warning",
  [PRE_SALE_STATUS.APPROVED]: "success",
  [PRE_SALE_STATUS.REJECTED]: "danger",
  [PRE_SALE_STATUS.IN_ANALYSIS]: "info",
};

// Interface para criar pré-venda
export interface CreatePreSaleData {
  unity_id: number;
  seller_id: number;
  client_id?: number | null;
  lead_id?: number | null;
  sale_type_id: number;
  payment_forms: Array<{
    payment_form_id: number;
    value: number;
    payment_advance_id?: number;
  }>;
  contest_product_classifications: Array<{
    id: number;
    quantity: number;
  }>;
  address?: {
    addressName?: string;
    street?: string;
    number?: string;
    complement?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    latitude?: number | string;
    longitude?: number | string;
  };
  circuit_route_id?: string;
}

// Criar pré-venda (com upload de fotos)
export const createPreSale = (
  data: CreatePreSaleData,
  files: File[]
): apiResponseType => {
  const formData = new FormData();

  // Adicionar os dados como JSON
  formData.append("unity_id", data.unity_id.toString());
  formData.append("seller_id", data.seller_id.toString());

  if (data.client_id) {
    formData.append("client_id", data.client_id.toString());
  }

  if (data.lead_id) {
    formData.append("lead_id", data.lead_id.toString());
  }

  formData.append("sale_type_id", data.sale_type_id.toString());

  // Payment forms
  data.payment_forms.forEach((pf, index) => {
    formData.append(`payment_forms[${index}][payment_form_id]`, pf.payment_form_id.toString());
    formData.append(`payment_forms[${index}][value]`, pf.value.toString());
    if (pf.payment_advance_id) {
      formData.append(`payment_forms[${index}][payment_advance_id]`, pf.payment_advance_id.toString());
    }
  });

  // Products
  data.contest_product_classifications.forEach((p, index) => {
    formData.append(`contest_product_classifications[${index}][id]`, p.id.toString());
    formData.append(`contest_product_classifications[${index}][quantity]`, p.quantity.toString());
  });

  // Address
  if (data.address) {
    if (data.address.addressName) formData.append("address[addressName]", data.address.addressName);
    if (data.address.street) formData.append("address[street]", data.address.street);
    if (data.address.number) formData.append("address[number]", data.address.number);
    if (data.address.complement) formData.append("address[complement]", data.address.complement);
    if (data.address.neighborhood) formData.append("address[neighborhood]", data.address.neighborhood);
    if (data.address.city) formData.append("address[city]", data.address.city);
    if (data.address.state) formData.append("address[state]", data.address.state);
    if (data.address.zip_code) formData.append("address[zip_code]", data.address.zip_code);
    if (data.address.latitude && data.address.latitude !== "undefined") {
      formData.append("address[latitude]", data.address.latitude.toString());
    }
    if (data.address.longitude && data.address.longitude !== "undefined") {
      formData.append("address[longitude]", data.address.longitude.toString());
    }
  }

  if (data.circuit_route_id) {
    formData.append("circuit_route_id", data.circuit_route_id);
  }

  // Files (fotos do bolão)
  files.forEach((file) => {
    formData.append("files[]", file);
  });

  return apiAuth.post("pre-sales/store", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// Buscar produtos disponíveis na unidade para pré-venda
export const getUnityProductsForPreSale = (
  unityId: number,
  page = 1
): apiResponseType => {
  const queryParams = new URLSearchParams();
  queryParams.append("page", page.toString());
  queryParams.append("unity_id", unityId.toString());

  return apiAuth.get(`pre-sales/unity-products?${queryParams.toString()}`);
};

// Buscar produtos de todas as unidades para pré-venda (admin)
export const getAllUnityProductsForPreSale = (page = 1): apiResponseType => {
  const queryParams = new URLSearchParams();
  queryParams.append("page", page.toString());

  return apiAuth.get(`pre-sales/all-unity-products?${queryParams.toString()}`);
};
